import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import NumberOfResultsPerPageFilter from 'components/NumberOfResultsPerPageFilter';
import useQueryParams from 'hooks/useQueryParams';
import {
  BOSS_MODE_TRACK_EVENTS,
  BOSS_MODE_TRACK_EVENTS_LABEL,
  BossModeStatsFilters,
  BossModeTrack,
  DEAL_FILTER_KEY,
  DEAL_TITLE_FILTER_KEY,
  EVENT_FILTER_KEY,
  LEAD_ID_FILTER_KEY,
  LEAD_NAME_FILTER_KEY,
  NEW_NDA_STATUS_FILTER_KEY,
  TIMEFRAME_FILTER,
  USER_ID_FILTER_KEY,
  USER_NAME_FILTER_KEY,
} from '../../types';
import useCreateSelectOptions from 'hooks/useCreateSelectOptions';
import useGetFilterOptions from 'hooks/useGetFilterOptions';
import useGetSearchableFilterOptions from '../../hooks';
import { useBossModeStatsLinks } from '../../useBossModeStatsLinks';
import {
  BossModeTrackTableKeys,
  CUSTOM_COL_SIZES,
  DEFAULT_SORT,
  HEADING_TRANSLATIONS,
  MULTI_SORT_VALUES,
  SORTING_VALUES,
  TABLE_COLUMNS,
  LOCKED_COLUMN,
} from '../../components/BossModeStatsTableCells/data';
import { BODY_CONTENT_GENERATE } from '../../components/BossModeStatsTableCells';
import { EMPTY_FILTER_VALUE, FilterOption } from 'models/Filters';
import { SORTING_ORDER, SortingDirectionValues } from 'models/Sort';
import { ColumnFilterOption } from 'components/ReorderResizeTable/types';
import useNumberOfResultsPerPage from 'hooks/useNumberOfResultsPerPage';
import { TIMEFRAME_PARAM_KEYWORD } from 'data/global';
import api from 'api';
import ReorderResizeTable from 'components/ReorderResizeTable';
import ActiveFilterSortPills from 'components/ActiveFilterSortPills';
import MultiFilterPanel from 'components/MultiFilterPanel';
import MultiSortPanel from 'components/MultiSortPanel';
import ManageColumns from 'components/ManageColumns';
import Subnav from 'components/Subnav';
import { TABLES_WITH_COLUMN_SETTINGS } from 'models/User';
import useTableColumnSettings from 'components/ReorderResizeTable/hooks/useTableColumnSettings';
import useTableFetchState from 'hooks/useTableFetchState';
import TableExportPanel from 'components/TableExportPanel';
import { getTableExportRowsAndColumnsParams } from 'components/TableExportPanel/TableExportPanel.utils';
import { TableExportSelection } from 'models/TableExport';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import DownloadStatusContext from 'providers/DownloadStatus/DownloadStatus.context';
import downloadService from 'services/downloadService';
import { getArrayLength, getHasActiveFilters, getIsArrayEmpty } from 'utils';
import useChipLabelClickRefs from 'hooks/useChipLabelClickRefs';

import './Forensics.styles.scss';

type ForensicsProps = {
  className?: string;
};

const Forensics: React.FC<ForensicsProps> = (props) => {
  const { className } = props;

  const {
    isLoading,
    totalPages,
    totalItems,
    data: items,
    setIsLoading,
    setPaginatedData,
  } = useTableFetchState<BossModeTrack>();

  const { popServerError } = useContext(PopNotificationsContext);
  const {
    isBossModeExportInProgress,
    startBossModeExportDownload,
    failBossModeExportDownload,
  } = useContext(DownloadStatusContext);

  const { limit, updateLimit } = useNumberOfResultsPerPage();

  const {
    params,
    params: {
      page,
      sort = DEFAULT_SORT,
      eventType,
      newNdaStatus,
      previousNdaStatus,
      userId,
      dealRoomIds,
      buyerIds,
      timeframe,
    },
  } = useQueryParams<BossModeStatsFilters & { sort: string }>();

  const {
    multiFilterRef,
    multiSortRef,
    onChipLabelClick,
    onSortChipLabelClick,
  } = useChipLabelClickRefs();

  const classes = classNames('forensics', className);

  const { t } = useTranslation();

  const createSelectOptions = useCreateSelectOptions();
  const { getMultiSelectSearchableFilter } = useGetFilterOptions();

  const { ndaOptions, getDealOptions, getLeadsOptions, getBMUserOptions } =
    useGetSearchableFilterOptions();

  const links = useBossModeStatsLinks();

  const {
    columns,
    allColumns,
    hiddenColumns,
    columnSizing,
    isDefaultColumns,
    onColumnSizingChange,
    onReorder,
    saveColumnSettings,
    resetToDefault,
  } = useTableColumnSettings(
    TABLES_WITH_COLUMN_SETTINGS.FORENSICS,
    TABLE_COLUMNS,
  );

  const renderedContent = useMemo(
    () =>
      TABLE_COLUMNS.reduce(
        (acc, col) => ({
          ...acc,
          [col]: (stat: BossModeTrack) => BODY_CONTENT_GENERATE[col](stat),
        }),
        {},
      ),
    [],
  );

  const eventTypeFilterOption = useMemo<FilterOption>(
    () => ({
      value: EVENT_FILTER_KEY,
      label: t('eventType'),
      isMultiple: true,
      options: Object.values(BOSS_MODE_TRACK_EVENTS).map((value) => ({
        value,
        label: t(BOSS_MODE_TRACK_EVENTS_LABEL[value]),
      })),
    }),
    [t],
  );

  const timeFilterOption = useMemo<FilterOption>(
    () => ({
      value: TIMEFRAME_PARAM_KEYWORD,
      label: t('time'),
      shouldSortOptions: false,
      options: createSelectOptions(Object.values(TIMEFRAME_FILTER)),
    }),
    [createSelectOptions, t],
  );

  const filterOptions = useMemo(
    () =>
      [
        eventTypeFilterOption,
        getMultiSelectSearchableFilter(
          t('bossMode'),
          USER_ID_FILTER_KEY,
          USER_NAME_FILTER_KEY,
          getBMUserOptions,
        ),
        getMultiSelectSearchableFilter(
          t('lead'),
          LEAD_ID_FILTER_KEY,
          LEAD_NAME_FILTER_KEY,
          getLeadsOptions,
          undefined,
          true,
          undefined,
          true,
        ),
        getMultiSelectSearchableFilter(
          t('deal'),
          DEAL_FILTER_KEY,
          DEAL_TITLE_FILTER_KEY,
          getDealOptions,
          undefined,
          true,
          undefined,
          true,
        ),
        {
          value: NEW_NDA_STATUS_FILTER_KEY,
          label: t('ndaStatus'),
          isMultiple: true,
          shouldSortOptions: false,
          options: [
            ...ndaOptions,
            {
              value: EMPTY_FILTER_VALUE,
              label: t('isEmpty'),
            },
          ],
        },
        timeFilterOption,
      ] as FilterOption[],
    [
      eventTypeFilterOption,
      ndaOptions,
      timeFilterOption,
      t,
      getMultiSelectSearchableFilter,
      getBMUserOptions,
      getLeadsOptions,
      getDealOptions,
    ],
  );

  const isEmpty = !isLoading && getIsArrayEmpty(items);

  const hasActiveFilters = useMemo(
    () => getHasActiveFilters(filterOptions, params),
    [filterOptions, params],
  );

  const filterOptionsByKey = useMemo(
    () =>
      filterOptions.reduce((acc, option) => {
        acc[option.value] = option;
        return acc;
      }, {} as Record<string, FilterOption>),
    [filterOptions],
  );

  const translatedSortOptions = useMemo(
    () =>
      MULTI_SORT_VALUES.map(({ value, label }) => ({ value, label: t(label) })),
    [t],
  );

  const multiSortDirectionTranslation = useMemo<SortingDirectionValues>(
    () => ({
      newNdaStatus: {
        [SORTING_ORDER.A_TO_Z]: t('leastProgressedFirst'),
        [SORTING_ORDER.Z_TO_A]: t('mostProgressedFirst'),
      },
      createdAt: {
        [SORTING_ORDER.A_TO_Z]: t('oldestFirst'),
        [SORTING_ORDER.Z_TO_A]: t('recentFirst'),
      },
    }),
    [t],
  );

  const columnFilterOptions = useMemo(
    () =>
      ({
        eventType: {
          options: filterOptionsByKey[EVENT_FILTER_KEY],
        },
        user: {
          options: filterOptionsByKey[USER_ID_FILTER_KEY],
        },
        leadNames: {
          options: filterOptionsByKey[LEAD_ID_FILTER_KEY],
        },
        dealRoomNames: {
          options: filterOptionsByKey[DEAL_FILTER_KEY],
        },
        previousNdaStatus: {
          options: filterOptionsByKey[NEW_NDA_STATUS_FILTER_KEY],
          sortDirectionTranslation: multiSortDirectionTranslation.newNdaStatus,
        },
        createdAt: {
          options: filterOptionsByKey[TIMEFRAME_PARAM_KEYWORD],
          sortDirectionTranslation: multiSortDirectionTranslation.createdAt,
        },
      } as Partial<Record<BossModeTrackTableKeys[number], ColumnFilterOption>>),
    [filterOptionsByKey, multiSortDirectionTranslation],
  );

  const onExport = useCallback(
    async ({ rows, columns: columnsScope, format }: TableExportSelection) => {
      try {
        startBossModeExportDownload();

        await api.portfolio.admin.exportBossModeTracks({
          eventType,
          newNdaStatus,
          previousNdaStatus,
          userId,
          dealRoomIds,
          buyerIds,
          timeframe,
          $sort: sort,
          ...getTableExportRowsAndColumnsParams({
            rows,
            columnsScope,
            columns,
            page,
            limit,
          }),
          format,
          uniqueId: downloadService.downloadUniqueId,
        });
      } catch (e) {
        popServerError(e);
        failBossModeExportDownload();
      }
    },
    [
      eventType,
      newNdaStatus,
      previousNdaStatus,
      userId,
      dealRoomIds,
      buyerIds,
      timeframe,
      sort,
      page,
      limit,
      columns,
      popServerError,
      startBossModeExportDownload,
      failBossModeExportDownload,
    ],
  );

  const getResultsCount = useCallback(
    ({
      eventType,
      userId,
      buyerIds,
      dealRoomIds,
      previousNdaStatus,
      newNdaStatus,
      timeframe,
    }: BossModeStatsFilters) =>
      api.portfolio.admin
        .getBossModeTracksCount({
          eventType,
          userId,
          buyerIds,
          dealRoomIds,
          previousNdaStatus,
          newNdaStatus,
          timeframe,
        })
        .then(({ data }) => data.count),
    [],
  );

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const { data } = await api.portfolio.admin.getBossModeTracks({
          $page: page,
          $limit: limit,
          $sort: sort,
          eventType,
          newNdaStatus,
          previousNdaStatus,
          userId,
          dealRoomIds,
          buyerIds,
          timeframe,
        });
        setPaginatedData(data);
      } catch (e) {
        return;
      } finally {
        setIsLoading(false);
      }
    })();
  }, [
    page,
    limit,
    sort,
    eventType,
    newNdaStatus,
    previousNdaStatus,
    userId,
    dealRoomIds,
    buyerIds,
    timeframe,
    setPaginatedData,
    setIsLoading,
  ]);

  return (
    <div className={classes}>
      <div className="operations-row">
        <Subnav links={links} />

        <div className="filter-wrapper">
          {(!isEmpty || hasActiveFilters) && (
            <>
              {!isEmpty && (
                <NumberOfResultsPerPageFilter
                  limit={limit}
                  updateLimit={updateLimit}
                />
              )}
              <MultiFilterPanel
                ref={multiFilterRef}
                filters={filterOptions}
                getResultsCount={getResultsCount}
              />
              <MultiSortPanel
                ref={multiSortRef}
                options={translatedSortOptions}
                sortDirectionTranslations={multiSortDirectionTranslation}
              />
            </>
          )}
          <ManageColumns
            allColumns={allColumns}
            hiddenColumns={hiddenColumns}
            headingTranslations={HEADING_TRANSLATIONS}
            lockedColumn={LOCKED_COLUMN}
            isDefaultColumns={isDefaultColumns}
            onSave={saveColumnSettings}
            onResetToDefault={resetToDefault}
          />
          <TableExportPanel
            totalItems={totalItems}
            pageItemsCount={getArrayLength(items)}
            totalPages={totalPages}
            totalColumnsCount={getArrayLength(TABLE_COLUMNS)}
            visibleColumnsCount={getArrayLength(columns)}
            isExporting={isBossModeExportInProgress}
            onExport={onExport}
          />
        </div>
      </div>

      <ActiveFilterSortPills
        filterOptions={filterOptions}
        sortOptions={translatedSortOptions}
        onChipLabelClick={onChipLabelClick}
        onSortChipLabelClick={onSortChipLabelClick}
      />

      <ReorderResizeTable
        columns={columns}
        data={items}
        sort={sort}
        isDataLoading={isLoading}
        totalPages={totalPages}
        totalItems={totalItems}
        recordName="event"
        customSizes={CUSTOM_COL_SIZES}
        headingTranslations={HEADING_TRANSLATIONS}
        tableContentTransformations={renderedContent}
        sortingValues={SORTING_VALUES}
        columnFilterOptions={columnFilterOptions}
        columnSizing={columnSizing}
        onColumnSizingChange={onColumnSizingChange}
        onReorder={onReorder as (columns: Array<keyof BossModeTrack>) => void}
      />
    </div>
  );
};

export default Forensics;
